import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import * as L from 'leaflet';
import { LabelGenerationService } from '../../../services/label-generation.service';
import { NotificationService } from '../../../services/notification.service';
import { WaybillDocumentService } from '../../../services/waybill-document.service';
import { ShipmentManagementService } from '../../../services/shipment-management.service';
import { ComWaybill, WaybillEventPresentation, WAYBILL_EVENT_PRESENTATIONS } from '../../../interfaces/ComWaybill.interfaces';
import { Image, IMAGE_TYPE_LABELS } from '../../../interfaces/Image.interfaces';
import { Webhook } from '../../../interfaces/Webhook.interfaces';

@Component({
  selector: 'app-waybill-detail-popup',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './waybill-detail-popup.component.html',
  styleUrl: './waybill-detail-popup.component.css'
})
export class WaybillDetailPopupComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @Input() visible: boolean = false;
  @Input() waybillCode: string = '';
  @Input() code: string = '';
  @Input() type: string = 'waybill';
  @Input() waybill: any = null; // Full waybill data for map
  @Input() dealer: any = null; // Dealer data for destination coordinates
  @Input() client: any = null; 
  @Input() allParcels: any[] = []; // All parcels of the waybill for label generation
  
  // Anagrafica corrieri per risolvere il codice corriere in nome
  @Input() carriers: any[] = [];

  // Anagrafiche per risolvere i codici in nomi completi
  @Input() warehouses: any[] = [];
  @Input() brands: any[] = [];
  
  @Output() closed = new EventEmitter<void>();
  @Output() tabChanged = new EventEmitter<string>();
  @Output() openAxylogPopup = new EventEmitter<string[]>(); // Emette array di waybillCodes per aprire il popup Axylog
  @Output() openMailPopup = new EventEmitter<string[]>(); // Emette array di waybillCodes per aprire il popup Mail

  @ViewChild('barcodeCanvas', { static: false }) barcodeCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('qrcodeCanvas', { static: false }) qrcodeCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef;

  activeTab: string = 'details';
  codeType: 'barcode' | 'qrcode' = 'barcode';
  private map: L.Map | null = null;

  // Eventi (Com_Waybills) tab properties
  waybillEvents: ComWaybill[] = [];
  isLoadingEvents: boolean = false;
  private eventsLoadedForCode: string | null = null;
  expandedEventNotes = new Set<number>(); // Indici degli eventi con nota espansa
  readonly eventNotePreviewLength = 250; // Massimo caratteri nota prima del "Mostra di più"

  // Immagini (Opr_Images) tab properties
  images: Image[] = [];
  isLoadingImages: boolean = false;
  private imagesLoadedForCode: string | null = null;
  imagesViewMode: 'grid' | 'list' = 'list'; // Compatta (griglia) o una sotto l'altra (lista)
  previewImage: Image | null = null; // Immagine aperta in anteprima

  // Webhook (Opr_Webhooks) tab properties
  webhooks: Webhook[] = [];
  isLoadingWebhooks: boolean = false;
  private webhooksLoadedForCode: string | null = null;
  expandedWebhooks = new Set<Webhook>(); // Webhook con contenuto espanso
  webhooksViewMode: 'cards' | 'table' = 'cards'; // Card (default) o tabella
  sortedWebhooks: Webhook[] = []; // Webhook nell'ordine della tabella
  webhooksSortColumn: string | null = null; // Colonna di ordinamento della tabella (null = ordine del backend)
  webhooksSortDirection: 'asc' | 'desc' = 'asc';

  // Email manager properties
  isEmailManagerVisible: boolean = false;
  trackingEmails: string[] = [];
  newEmailInput: string = '';
  
  // Recalculate delivery date property
  isRecalculating: boolean = false;

  constructor(
    private labelGenerationService: LabelGenerationService,
    private notificationService: NotificationService,
    private waybillDocumentService: WaybillDocumentService,
    private shipmentManagementService: ShipmentManagementService
  ) {}

  get displayCode(): string {
    return this.code || this.waybillCode;
  }

  get displayType(): string {
    switch (this.type) {
      case 'waybill':
        return 'Bolla';
      case 'ragione-sociale':
        return 'Ragione Sociale';
      default:
        return 'Bolla';
    }
  }

  ngOnInit(): void {
    document.addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reset degli eventi e delle immagini quando cambia la waybill
    if (changes['waybill'] && !changes['waybill'].firstChange) {
      this.waybillEvents = [];
      this.eventsLoadedForCode = null;
      this.expandedEventNotes.clear();
      this.images = [];
      this.imagesLoadedForCode = null;
      this.previewImage = null;
      this.webhooks = [];
      this.sortedWebhooks = [];
      this.webhooksLoadedForCode = null;
      this.expandedWebhooks.clear();
    }

    // Quando il popup diventa visibile o cambiano i dati della waybill, ricalcola le email
    if ((changes['visible'] && changes['visible'].currentValue === true) || changes['waybill']) {
      this.loadEmailsFromNotes();

      // Aggiorna il contenuto della tab attiva se il popup è visibile
      if (this.visible) {
        if (this.activeTab === 'barcode') {
          setTimeout(() => this.generateCode(), 100);
        } else if (this.activeTab === 'map' && this.hasValidCoordinates()) {
          setTimeout(() => this.initializeMap(), 100);
        } else if (this.activeTab === 'events') {
          this.loadEvents();
        } else if (this.activeTab === 'images') {
          this.loadImages();
        } else if (this.activeTab === 'webhooks') {
          this.loadWebhooks();
        }
      }
    }
    
    // Aggiorna anche quando cambiano code/waybillCode/dealer per barcode/map
    if ((changes['code'] || changes['waybillCode']) && this.visible && this.activeTab === 'barcode') {
      setTimeout(() => this.generateCode(), 100);
    }
    
    if ((changes['dealer'] || changes['waybill']) && this.visible && this.activeTab === 'map' && this.hasValidCoordinates()) {
      setTimeout(() => this.initializeMap(), 100);
    }
  }

  ngAfterViewInit(): void {
    if (this.activeTab === 'barcode') {
      setTimeout(() => this.generateCode(), 100);
    } else if (this.activeTab === 'map' && this.hasValidCoordinates()) {
      setTimeout(() => this.initializeMap(), 100);
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('keydown', this.handleKeyDown.bind(this));
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.visible) {
      // Se è aperta l'anteprima immagine, chiudi prima quella
      if (this.previewImage) {
        this.closeImagePreview();
        return;
      }
      this.close();
    }
  }

  close(): void {
    this.closed.emit();
  }

  /** Risolve un codice magazzino in "Nome (CODICE)" (fallback: il codice stesso) */
  getWarehouseName(code: string | undefined | null): string {
    if (!code) return '-';
    const warehouse = this.warehouses?.find(w => w.warehouseCode === code);
    return warehouse?.warehouseName ? `${warehouse.warehouseName} (${code})` : code;
  }

  /** Risolve un codice vettore in "Nome (CODICE)" (fallback: il codice stesso) */
  getCarrierName(code: string | undefined | null): string {
    if (!code) return '-';
    const carrier = this.carriers?.find(c => c.carrierCode === code);
    return carrier?.carrierName ? `${carrier.carrierName} (${code})` : code;
  }

  /** Risolve un codice brand in "Nome (CODICE)" (fallback: il codice stesso) */
  getBrandName(code: string | undefined | null): string {
    if (!code) return '-';
    const brand = this.brands?.find(b => b.brandCode === code);
    return brand?.brandName ? `${brand.brandName} (${code})` : code;
  }

  copyToClipboard(text: string): void {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        console.log('Text copied to clipboard:', text);
      }).catch(err => {
        console.error('Failed to copy text: ', err);
      });
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        console.log('Text copied to clipboard:', text);
      } catch (err) {
        console.error('Fallback: Unable to copy', err);
      }
      document.body.removeChild(textArea);
    }
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
    this.tabChanged.emit(tab);

    if (tab === 'barcode') {
      setTimeout(() => this.generateCode(), 100);
    } else if (tab === 'map' && this.hasValidCoordinates()) {
      setTimeout(() => this.initializeMap(), 100);
    } else if (tab === 'events') {
      this.loadEvents();
    } else if (tab === 'images') {
      this.loadImages();
    } else if (tab === 'webhooks') {
      this.loadWebhooks();
    }
  }

  /**
   * Recupera i webhook della bolla (Opr_Webhooks) dall'endpoint /waybill-webhooks
   * (ordinati dal backend per data di creazione decrescente)
   */
  loadWebhooks(): void {
    const code = this.waybill?.waybillCode || this.displayCode;
    if (!code) {
      this.notificationService.error('Nessun codice bolla disponibile');
      return;
    }

    // Se i webhook sono già stati caricati per questa bolla, non ricaricarli
    if (this.webhooksLoadedForCode === code && !this.isLoadingWebhooks) {
      return;
    }

    this.isLoadingWebhooks = true;

    this.shipmentManagementService.getWaybillWebhooks(code)
      .subscribe({
        next: (webhooks) => {
          this.webhooks = webhooks || [];
          this.updateSortedWebhooks();
          this.webhooksLoadedForCode = code;
          this.expandedWebhooks.clear();
          this.isLoadingWebhooks = false;
        },
        error: (error) => {
          console.error('Error loading waybill webhooks:', error);
          this.notificationService.error('Errore nel caricamento dei webhook della bolla');
          this.webhooks = [];
          this.sortedWebhooks = [];
          this.isLoadingWebhooks = false;
        }
      });
  }

  /**
   * Classe CSS di stato per un webhook (RECEIVED / PROCESSED / REJECTED)
   */
  getWebhookStatusClass(webhook: Webhook): string {
    const status = (webhook.webhookStatus || '').toUpperCase();
    if (status === 'PROCESSED') return 'status-executed';
    if (status === 'REJECTED') return 'status-failed';
    return 'status-in-progress';
  }

  /**
   * Presentazione del tipo di webhook (tipologia del dato, icona, colore). Null se non mappato.
   */
  private getWebhookTypePresentation(webhook: Webhook): { label: string; icon: string; variant: string } | null {
    const presentations: Record<string, { label: string; icon: string; variant: string }> = {
      photos: { label: 'Messaggio di Immagine', icon: 'fa-camera', variant: 'blue' },
      deliveries: { label: 'Messaggio di Consegna', icon: 'fa-check-circle', variant: 'green' },
      pickups: { label: 'Messaggio di Presa in Carico', icon: 'fa-truck-loading', variant: 'purple' },
      picking: { label: 'Picking di Magazzino', icon: 'fa-warehouse', variant: 'orange' }
    };
    return presentations[(webhook.webhookEventType || '').trim().toLowerCase()] || null;
  }

  /**
   * Tipologia del dato in base al tipo di webhook (null se non mappato)
   */
  getWebhookTypeLabel(webhook: Webhook): string | null {
    return this.getWebhookTypePresentation(webhook)?.label || null;
  }

  /**
   * Icona FontAwesome del tipo di webhook (fallback: fa-plug)
   */
  getWebhookTypeIcon(webhook: Webhook): string {
    return this.getWebhookTypePresentation(webhook)?.icon || 'fa-plug';
  }

  /**
   * Classe colore della tipologia del dato: colore del tipo se PROCESSED,
   * rosso se REJECTED, arancione altrimenti (RECEIVED)
   */
  getWebhookBadgeClass(webhook: Webhook): string {
    const status = (webhook.webhookStatus || '').toUpperCase();
    if (status === 'PROCESSED') {
      return `badge-variant--${this.getWebhookTypePresentation(webhook)?.variant || 'green'}`;
    }
    return status === 'REJECTED' ? 'badge-variant--red' : 'badge-variant--orange';
  }

  /**
   * Classe colore del marker: variante del tipo se mappato, altrimenti classe di stato
   */
  getWebhookVariantClass(webhook: Webhook): string {
    const presentation = this.getWebhookTypePresentation(webhook);
    return presentation ? `event-variant--${presentation.variant}` : this.getWebhookStatusClass(webhook);
  }

  /**
   * True se il contenuto del webhook è espanso
   */
  isWebhookExpanded(webhook: Webhook): boolean {
    return this.expandedWebhooks.has(webhook);
  }

  /**
   * Espande/comprime il contenuto del webhook
   */
  toggleWebhook(webhook: Webhook): void {
    if (this.expandedWebhooks.has(webhook)) {
      this.expandedWebhooks.delete(webhook);
    } else {
      this.expandedWebhooks.add(webhook);
    }
  }

  /**
   * Ordinamento della tabella a 3 click: crescente, decrescente, ordine originale
   */
  sortWebhooks(column: string): void {
    if (this.webhooksSortColumn !== column) {
      this.webhooksSortColumn = column;
      this.webhooksSortDirection = 'asc';
    } else if (this.webhooksSortDirection === 'asc') {
      this.webhooksSortDirection = 'desc';
    } else {
      this.webhooksSortColumn = null;
      this.webhooksSortDirection = 'asc';
    }
    this.updateSortedWebhooks();
  }

  /**
   * Icona di ordinamento della colonna
   */
  getWebhookSortIcon(column: string): string {
    if (this.webhooksSortColumn !== column) return 'fa-sort';
    return this.webhooksSortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down';
  }

  /**
   * Ricalcola l'ordine della tabella (senza colonna: ordine del backend)
   */
  private updateSortedWebhooks(): void {
    const column = this.webhooksSortColumn;
    if (!column) {
      this.sortedWebhooks = [...this.webhooks];
      return;
    }

    const direction = this.webhooksSortDirection === 'asc' ? 1 : -1;
    const dateColumns = ['webhookCreatedAt', 'webhookUpdatedAt'];
    const valueOf = (webhook: Webhook): string | number | null => {
      if (column === 'typeLabel') return this.getWebhookTypeLabel(webhook);
      const value = (webhook as any)[column];
      if (value === null || value === undefined || value === '') return null;
      if (dateColumns.includes(column)) return new Date(value).getTime();
      return typeof value === 'number' ? value : String(value);
    };

    this.sortedWebhooks = [...this.webhooks].sort((a, b) => {
      const va = valueOf(a);
      const vb = valueOf(b);
      // Valori vuoti sempre in fondo
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * direction;
      return String(va).localeCompare(String(vb), 'it', { numeric: true, sensitivity: 'base' }) * direction;
    });
  }

  /**
   * Contenuto JSON formattato (fallback: il testo grezzo)
   */
  formatWebhookJson(content: string | undefined): string {
    if (!content) return '';
    try {
      return JSON.stringify(JSON.parse(content), null, 2);
    } catch {
      return content;
    }
  }

  /**
   * Recupera le immagini della bolla (Opr_Images) dall'endpoint /waybill-images
   */
  loadImages(): void {
    const code = this.waybill?.waybillCode || this.displayCode;
    if (!code) {
      this.notificationService.error('Nessun codice bolla disponibile');
      return;
    }

    // Se le immagini sono già state caricate per questa bolla, non ricaricarle
    if (this.imagesLoadedForCode === code && !this.isLoadingImages) {
      return;
    }

    this.isLoadingImages = true;

    this.shipmentManagementService.getWaybillImages(code)
      .subscribe({
        next: (images) => {
          // Ordina dalla più recente alla più vecchia
          this.images = (images || []).sort((a, b) =>
            new Date(b.imageCreatedAt || 0).getTime() - new Date(a.imageCreatedAt || 0).getTime());
          this.imagesLoadedForCode = code;
          this.isLoadingImages = false;
        },
        error: (error) => {
          console.error('Error loading waybill images:', error);
          this.notificationService.error('Errore nel caricamento delle immagini della bolla');
          this.images = [];
          this.isLoadingImages = false;
        }
      });
  }

  /**
   * Etichetta italiana del tipo immagine (fallback: il tipo grezzo)
   */
  getImageTypeLabel(image: Image): string {
    const type = (image.imageType || '').toUpperCase();
    return IMAGE_TYPE_LABELS[type] || image.imageType || 'Immagine';
  }

  /**
   * True se il contenuto è un PDF (non visualizzabile come <img>)
   */
  isPdf(image: Image): boolean {
    return (image.imageMimeType || '').toLowerCase() === 'application/pdf'
      || (image.imageExtension || '').toLowerCase().includes('pdf')
      || (image.imageType || '').toUpperCase() === 'POD_PDF';
  }

  /**
   * Contenuto Base64 dell'immagine, ripulito da eventuali spazi/newline
   */
  private getImageBase64(image: Image): string | null {
    const raw = image.imageContentBase64 || image.imageContentByte;
    return raw ? raw.replace(/\s/g, '') : null;
  }

  /**
   * Data URL per visualizzare l'immagine in un tag <img> (null per PDF o senza contenuto)
   */
  getImageSrc(image: Image): string | null {
    if (this.isPdf(image)) {
      return null;
    }
    const base64 = this.getImageBase64(image);
    if (!base64) {
      return null;
    }
    return `data:${image.imageMimeType || 'image/jpeg'};base64,${base64}`;
  }

  /**
   * Dimensione file leggibile (B/KB/MB)
   */
  formatImageSize(bytes: number | undefined): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  /**
   * Converte una stringa Base64 in Blob
   */
  private base64ToBlob(base64: string, mimeType: string): Blob {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], {type: mimeType});
  }

  /**
   * Apre l'anteprima dell'immagine (i PDF si aprono in una nuova scheda)
   */
  openImagePreview(image: Image): void {
    if (this.isPdf(image)) {
      const base64 = this.getImageBase64(image);
      if (!base64) {
        this.notificationService.error('Contenuto del documento non disponibile');
        return;
      }
      try {
        const blob = this.base64ToBlob(base64, 'application/pdf');
        window.open(URL.createObjectURL(blob), '_blank');
      } catch (error) {
        console.error('Error opening PDF:', error);
        this.notificationService.error('Errore nell\'apertura del documento');
      }
      return;
    }

    if (!this.getImageSrc(image)) {
      this.notificationService.warning('Contenuto dell\'immagine non disponibile');
      return;
    }
    this.previewImage = image;
  }

  /**
   * Chiude l'anteprima dell'immagine
   */
  closeImagePreview(): void {
    this.previewImage = null;
  }

  /**
   * Scarica l'immagine come file
   */
  downloadImage(image: Image): void {
    const base64 = this.getImageBase64(image);
    if (!base64) {
      this.notificationService.error('Contenuto dell\'immagine non disponibile');
      return;
    }

    try {
      const mimeType = image.imageMimeType || 'application/octet-stream';
      const blob = this.base64ToBlob(base64, mimeType);

      // Nome file: waybillCode_tipo_timestamp.ext
      const code = image.imageWaybillCode || this.waybill?.waybillCode || this.displayCode || 'waybill';
      const type = (image.imageType || 'IMG').toUpperCase();
      const timestamp = new Date().toISOString()
        .replace(/[:.]/g, '-')
        .substring(0, 19);
      const extension = (image.imageExtension || '').replace('.', '')
        || (mimeType.includes('/') ? mimeType.split('/').pop() : '') || '';
      const fileName = `${code}_${type}_${timestamp}${extension ? '.' + extension : ''}`;

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading image:', error);
      this.notificationService.error('Errore nel download dell\'immagine');
    }
  }

  /**
   * Recupera gli eventi complementari della bolla (Com_Waybills)
   * dall'endpoint /waybill-event (ordinati per data di creazione crescente)
   */
  loadEvents(): void {
    const waybillCode = this.waybill?.waybillCode || this.displayCode;
    const waybillShipmentCode = this.waybill?.waybillShipmentCode;

    if (!waybillCode || !waybillShipmentCode) {
      this.notificationService.error('Codice bolla o codice spedizione non disponibile');
      return;
    }

    // Se gli eventi sono già stati caricati per questa bolla, non ricaricarli
    if (this.eventsLoadedForCode === waybillCode && !this.isLoadingEvents) {
      return;
    }

    this.isLoadingEvents = true;

    this.shipmentManagementService.getWaybillEvents(waybillCode, waybillShipmentCode)
      .subscribe({
        next: (events) => {
          // Ordina dal più recente al più vecchio (l'evento più nuovo in cima alla timeline)
          this.waybillEvents = (events || []).sort((a, b) =>
            new Date(b.waybillComplementaryCreatedAt || 0).getTime() - new Date(a.waybillComplementaryCreatedAt || 0).getTime());
          this.eventsLoadedForCode = waybillCode;
          this.expandedEventNotes.clear();
          this.isLoadingEvents = false;
        },
        error: (error) => {
          console.error('Error loading waybill events:', error);
          this.notificationService.error('Errore nel caricamento degli eventi della bolla');
          this.waybillEvents = [];
          this.isLoadingEvents = false;
        }
      });
  }

  /**
   * Data dell'ultimo evento della bolla (il più recente è in cima alla lista)
   */
  get lastEventDate(): string | undefined {
    return this.waybillEvents.length > 0
      ? this.waybillEvents[0].waybillComplementaryCreatedAt
      : undefined;
  }

  /**
   * Transcodifica del nome evento nel relativo modello di presentazione
   * (etichetta italiana, icona, colore). Null se il nome non è mappato.
   */
  private getEventPresentation(event: ComWaybill): WaybillEventPresentation | null {
    const name = (event.waybillComplementaryName || '').trim().toUpperCase();
    return WAYBILL_EVENT_PRESENTATIONS[name] || null;
  }

  /**
   * Etichetta dell'evento: transcodifica italiana se mappata,
   * altrimenti il nome grezzo (fallback: tipo o 'Evento')
   */
  getEventLabel(event: ComWaybill): string {
    const presentation = this.getEventPresentation(event);
    return presentation?.label || event.waybillComplementaryName || event.waybillComplementaryType || 'Evento';
  }

  /**
   * Icona FontAwesome per un evento: dalla transcodifica se mappata,
   * altrimenti euristica su tipo/nome
   */
  getEventIcon(event: ComWaybill): string {
    const presentation = this.getEventPresentation(event);
    if (presentation) {
      return presentation.icon;
    }

    const type = `${event.waybillComplementaryType || ''} ${event.waybillComplementaryName || ''}`.toUpperCase();
    if (type.includes('DELIVER') || type.includes('CONSEGN')) return 'fa-check-circle';
    if (type.includes('MAIL') || type.includes('EMAIL')) return 'fa-envelope';
    if (type.includes('AXYLOG')) return 'fa-cloud-upload-alt';
    if (type.includes('PDF') || type.includes('PRINT') || type.includes('STAMPA')) return 'fa-file-pdf';
    if (type.includes('CARRIER') || type.includes('VETTORE') || type.includes('TRANSPORT')) return 'fa-truck';
    if (type.includes('STATUS') || type.includes('STATO')) return 'fa-exchange-alt';
    if (type.includes('NOTE') || type.includes('NOTA')) return 'fa-sticky-note';
    return 'fa-history';
  }

  /**
   * Classe colore del marker della timeline: variante dalla transcodifica
   * se mappata, altrimenti classe derivata dallo stato
   */
  getEventVariantClass(event: ComWaybill): string {
    const presentation = this.getEventPresentation(event);
    return presentation ? `event-variant--${presentation.variant}` : this.getEventStatusClass(event);
  }

  /**
   * Classe CSS di stato per un evento
   */
  getEventStatusClass(event: ComWaybill): string {
    const status = (event.waybillComplementaryStatus || '').toUpperCase();
    if (['OK', 'COMPLETED', 'EXECUTED', 'SUCCESS', 'DELIVERED', 'ACTIVE'].includes(status)) return 'status-executed';
    if (['ERROR', 'FAILED', 'KO'].includes(status)) return 'status-failed';
    return 'status-in-progress';
  }

  /**
   * True se la nota dell'evento supera la lunghezza di anteprima
   */
  isEventNoteTruncatable(event: ComWaybill): boolean {
    return (event.waybillComplementaryValueClob || '').length > this.eventNotePreviewLength;
  }

  /**
   * True se la nota dell'evento è espansa
   */
  isEventNoteExpanded(index: number): boolean {
    return this.expandedEventNotes.has(index);
  }

  /**
   * Espande/comprime la nota dell'evento
   */
  toggleEventNote(index: number): void {
    if (this.expandedEventNotes.has(index)) {
      this.expandedEventNotes.delete(index);
    } else {
      this.expandedEventNotes.add(index);
    }
  }

  /**
   * Nota dell'evento: completa se breve o espansa, altrimenti
   * troncata alla lunghezza di anteprima
   */
  getEventNote(event: ComWaybill, index: number): string {
    const note = event.waybillComplementaryValueClob || '';
    if (note.length <= this.eventNotePreviewLength || this.isEventNoteExpanded(index)) {
      return note;
    }
    return note.substring(0, this.eventNotePreviewLength) + '…';
  }

  /**
   * Valore dell'evento: testo, numero o data (il primo valorizzato)
   */
  getEventValue(event: ComWaybill): string | null {
    if (event.waybillComplementaryValueText) {
      return event.waybillComplementaryValueText;
    }
    if (event.waybillComplementaryValueNumber !== null && event.waybillComplementaryValueNumber !== undefined) {
      return String(event.waybillComplementaryValueNumber);
    }
    if (event.waybillComplementaryValueDate) {
      const date = new Date(event.waybillComplementaryValueDate);
      return isNaN(date.getTime()) ? event.waybillComplementaryValueDate : date.toLocaleString('it-IT');
    }
    return null;
  }

  setCodeType(type: 'barcode' | 'qrcode'): void {
    this.codeType = type;
    setTimeout(() => this.generateCode(), 100);
  }

  private generateCode(): void {
    if (!this.displayCode) return;

    if (this.codeType === 'barcode' && this.barcodeCanvas) {
      try {
        JsBarcode(this.barcodeCanvas.nativeElement, this.displayCode, {
          format: 'CODE128',
          width: 2,
          height: 100,
          displayValue: true,
          fontSize: 14,
          margin: 10
        });
      } catch (error) {
        console.error('Error generating barcode:', error);
      }
    } else if (this.codeType === 'qrcode' && this.qrcodeCanvas) {
      try {
        QRCode.toCanvas(this.qrcodeCanvas.nativeElement, this.displayCode, {
          width: 200,
          margin: 2,
          errorCorrectionLevel: 'M'
        });
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    }
  }

  hasValidCoordinates(): boolean {
    // Check if dealer has coordinates (minimum requirement to show map)
    return !!(this.dealer?.dealerLatitude && this.dealer?.dealerLongitude);
  }

  isDelivered(): boolean {
    const deliveredStatuses = ['DELIVERED'];
    return deliveredStatuses.includes(this.waybill?.waybillStatus?.toUpperCase() || '');
  }

  /**
   * Verifica se la bolla è in ritardo: non ancora consegnata e con data/ora odierna
   * che ha superato la data prevista di consegna.
   * @returns true se in ritardo, false altrimenti
   */
  isLate(): boolean {
    if (this.isDelivered() || !this.waybill?.waybillEstimatedDeliveryDate) {
      return false;
    }
    const estimated = new Date(this.waybill.waybillEstimatedDeliveryDate);
    if (isNaN(estimated.getTime())) {
      return false;
    }
    return new Date() > estimated;
  }

  /**
   * Verifica se la bolla ha coordinate di consegna
   * @returns true se ci sono coordinate, false altrimenti
   */
  hasDeliveryCoordinates(): boolean {
    return !!(this.waybill?.waybillLatitude && this.waybill?.waybillLongitude);
  }

  /**
   * Calcola la distanza in metri tra due coordinate usando la formula di Haversine
   * @param lat1 Latitudine punto 1
   * @param lon1 Longitudine punto 1
   * @param lat2 Latitudine punto 2
   * @param lon2 Longitudine punto 2
   * @returns Distanza in metri
   */
  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371e3; // Raggio della Terra in metri
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distanza in metri
  }

  /**
   * Verifica se la consegna è avvenuta entro il raggio di this.client.clientMaximumRange m dal destinatario
   * @returns true se entro this.client.clientMaximumRange m, false altrimenti
   */
  isDeliveryWithinRadius(): boolean {
    const dealerLat = this.dealer?.dealerLatitude;
    const dealerLng = this.dealer?.dealerLongitude;
    const waybillLat = this.waybill?.waybillLatitude;
    const waybillLng = this.waybill?.waybillLongitude;

    if (!dealerLat || !dealerLng || !waybillLat || !waybillLng) {
      return false;
    }

    const distance = this.calculateDistance(dealerLat, dealerLng, waybillLat, waybillLng);
    return distance <= this.client.clientMaximumRange;
  }

  /**
   * Ottiene la distanza dalla destinazione in metri
   * @returns Distanza in metri o null se non disponibile
   */
  getDistanceFromDestination(): number | null {
    const dealerLat = this.dealer?.dealerLatitude;
    const dealerLng = this.dealer?.dealerLongitude;
    const waybillLat = this.waybill?.waybillLatitude;
    const waybillLng = this.waybill?.waybillLongitude;

    if (!dealerLat || !dealerLng || !waybillLat || !waybillLng) {
      return null;
    }

    return this.calculateDistance(dealerLat, dealerLng, waybillLat, waybillLng);
  }

  /**
   * Ricalcola la data prevista di consegna per la waybill
   */
  recalculateEstimatedDeliveryDate(): void {
    if (this.isRecalculating) {
      return;
    }

    if (!this.waybill?.waybillCode || !this.waybill?.waybillShipmentCode) {
      this.notificationService.error('Codice bolla o codice spedizione non disponibile');
      return;
    }

    this.isRecalculating = true;

    this.shipmentManagementService
      .recalculateEstimatedDeliveryDate(this.waybill.waybillCode, this.waybill.waybillShipmentCode)
      .subscribe({
        next: (recalculatedDate) => {
          this.isRecalculating = false;
          this.waybill.waybillEstimatedDeliveryDate = recalculatedDate;
          this.notificationService.success('Data prevista ricalcolata con successo');
        },
        error: (error) => {
          this.isRecalculating = false;
          console.error('Error recalculating estimated delivery date:', error);
          this.notificationService.error('Errore nel ricalcolo della data prevista');
        }
      });
  }

  /**
   * Verifica se ci sono email valide nelle note della waybill
   * @returns true se ci sono email, false altrimenti
   */
  hasEmailsInNotes(): boolean {
    if (!this.waybill?.waybillNote) {
      return false;
    }

    // Regex per validare le email
    const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
    const emails = this.waybill.waybillNote.match(emailRegex);
    
    return emails !== null && emails.length > 0;
  }

  /**
   * Estrae le email dalle note della waybill
   * @returns Array di email trovate nelle note
   */
  extractEmailsFromNotes(): string[] {
    if (!this.waybill?.waybillNote) {
      return [];
    }

    const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
    const emails = this.waybill.waybillNote.match(emailRegex);
    
    return emails || [];
  }

  /**
   * Carica le email dalle note della waybill nell'array trackingEmails
   */
  loadEmailsFromNotes(): void {
    this.trackingEmails = this.extractEmailsFromNotes();
  }

  /**
   * Aggiorna le note della waybill con le email correnti, separate da punto e virgola
   */
  updateNotesWithEmails(): void {
    if (!this.waybill) {
      return;
    }

    // Rimuove tutte le email esistenti dalle note
    let updatedNotes = this.waybill.waybillNote || '';
    const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
    updatedNotes = updatedNotes.replace(emailRegex, '').trim();
    
    // Pulisce eventuali punto e virgola multipli o trailing
    updatedNotes = updatedNotes.replace(/;+/g, ';').replace(/^;|;$/g, '').trim();

    // Aggiunge le email correnti separate da punto e virgola
    if (this.trackingEmails.length > 0) {
      const emailsString = this.trackingEmails.join('; ');
      updatedNotes = updatedNotes ? `${updatedNotes}; ${emailsString}` : emailsString;
    }

    // Aggiorna le note della waybill
    this.waybill.waybillNote = updatedNotes;
  }

  /**
   * Apre il popup di gestione email
   */
  openEmailManager(): void {
    this.loadEmailsFromNotes();
    this.newEmailInput = '';
    this.isEmailManagerVisible = true;
  }

  /**
   * Chiude il popup di gestione email
   * Le modifiche alle email sono già state sincronizzate con le note della waybill
   */
  closeEmailManager(): void {
    this.isEmailManagerVisible = false;
    this.newEmailInput = '';
  }

  /**
   * Valida un indirizzo email
   * @param email Email da validare
   * @returns true se l'email è valida
   */
  isValidEmail(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }

  /**
   * Aggiunge una nuova email all'array trackingEmails e la sincronizza con le note
   */
  addEmail(): void {
    const email = this.newEmailInput.trim();
    
    if (!email) {
      return;
    }

    if (!this.isValidEmail(email)) {
      this.notificationService.error('Inserisci un indirizzo email valido');
      return;
    }

    if (this.trackingEmails.includes(email)) {
      this.notificationService.warning('Questa email è già presente nella lista');
      return;
    }

    this.trackingEmails.push(email);
    this.updateNotesWithEmails();
    this.newEmailInput = '';
    this.notificationService.success(`Email ${email} aggiunta con successo`);
  }

  /**
   * Rimuove un'email dall'array trackingEmails e la sincronizza con le note
   * @param index Indice dell'email da rimuovere
   */
  removeEmail(index: number): void {
    if (index >= 0 && index < this.trackingEmails.length) {
      const removedEmail = this.trackingEmails[index];
      this.trackingEmails.splice(index, 1);
      this.updateNotesWithEmails();
      this.notificationService.success(`Email ${removedEmail} rimossa`);
    }
  }

  /**
   * Gestisce la pressione del tasto Enter nel campo email
   * @param event Evento tastiera
   */
  onEmailKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addEmail();
    }
  }

  private initializeMap(): void {
    if (!this.mapContainer) {
      return;
    }

    // Require at least dealer coordinates
    const dealerLat = this.dealer?.dealerLatitude;
    const dealerLng = this.dealer?.dealerLongitude;
    
    if (!dealerLat || !dealerLng) {
      console.warn('Cannot initialize map: dealer coordinates missing');
      return;
    }

    if (this.map) {
      this.map.remove();
      this.map = null;
    }

    // Prepare waybill coordinates (optional)
    const waybillLat = this.waybill?.waybillLatitude;
    const waybillLng = this.waybill?.waybillLongitude;

    // Determine map center
    let centerLat = dealerLat;
    let centerLng = dealerLng;
    let zoomLevel = 16;

    // If both exist, center between them
    if (waybillLat && waybillLng) {
      centerLat = (dealerLat + waybillLat) / 2;
      centerLng = (dealerLng + waybillLng) / 2;
      zoomLevel = 15;
    }

    this.map = L.map(this.mapContainer.nativeElement, {
      center: [centerLat, centerLng],
      zoom: zoomLevel,
      zoomControl: true,
      attributionControl: true
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '| © OpenStreetMap',
      maxZoom: 19
    }).addTo(this.map);

    // Add custom control for dealer position
    const dealerControl = L.Control.extend({
      options: {
        position: 'topleft'
      },
      onAdd: (map: L.Map) => {
        const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
        const button = L.DomUtil.create('a', 'leaflet-control-dealer', container);
        button.innerHTML = '📍';
        button.href = '#';
        button.title = 'Centra su concessionario';
        button.setAttribute('role', 'button');
        button.setAttribute('aria-label', 'Centra su concessionario');
        
        L.DomEvent.disableClickPropagation(button);
        L.DomEvent.on(button, 'click', (e: Event) => {
          L.DomEvent.preventDefault(e);
          if (dealerLat && dealerLng) {
            map.setView([dealerLat, dealerLng], 14);
          }
        });
        
        return container;
      }
    });
    
    this.map.addControl(new dealerControl());

    // Add custom control for delivery position
    const deliveryControl = L.Control.extend({
      options: {
        position: 'topleft'
      },
      onAdd: (map: L.Map) => {
        const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
        const button = L.DomUtil.create('a', 'leaflet-control-delivery', container);
        button.innerHTML = '📄';
        button.href = '#';
        button.title = waybillLat && waybillLng ? 'Centra su punto di consegna' : 'Coordinate di consegna non disponibili';
        button.setAttribute('role', 'button');
        button.setAttribute('aria-label', 'Centra su punto di consegna');
        
        // Disable button if no delivery coordinates
        if (!waybillLat || !waybillLng) {
          button.classList.add('disabled');
          button.style.cursor = 'not-allowed';
          button.style.opacity = '0.5';
        }
        
        L.DomEvent.disableClickPropagation(button);
        L.DomEvent.on(button, 'click', (e: Event) => {
          L.DomEvent.preventDefault(e);
          if (waybillLat && waybillLng) {
            map.setView([waybillLat, waybillLng], 16);
          }
        });
        
        return container;
      }
    });
    
    this.map.addControl(new deliveryControl());

    // Add dealer marker (destination) if available
    if (dealerLat && dealerLng) {
      const dealerIcon = L.icon({
        iconUrl: 'assets/img/marker-custom.png',
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32]
      });

      const dealerMarker = L.marker([dealerLat, dealerLng], { icon: dealerIcon }).addTo(this.map);
      
      const dealerPopup = `
        <div class="map-popup">
          <strong>📍 Destinazione (Dealer)</strong><br>
          ${this.dealer?.dealerName || this.dealer?.dealerLegalName || 'Dealer'}<br>
          <small>Lat: ${dealerLat}, Lng: ${dealerLng}</small>
        </div>
      `;
      
      dealerMarker.bindPopup(dealerPopup);

      // Add this.client.clientMaximumRange (m) radius circle around dealer
      L.circle([dealerLat, dealerLng], {
        color: '#007bff',
        fillColor: '#007bff',
        fillOpacity: 0.1,
        radius: this.client.clientMaximumRange
      }).addTo(this.map).bindPopup(`Raggio di consegna: ${this.client.clientMaximumRange} m`);
    }

    // Add waybill marker (current location) if available
    if (waybillLat && waybillLng) {
      const waybillIcon = L.icon({
        iconUrl: 'assets/img/marker-custom.png',
        iconSize: [28, 28],
        iconAnchor: [14, 28],
        popupAnchor: [0, -28],
        className: 'waybill-marker'
      });

      const waybillMarker = L.marker([waybillLat, waybillLng], { icon: waybillIcon }).addTo(this.map);
      
      const waybillPopup = `
        <div class="map-popup">
          <strong>📄 Posizione Consegna Bolla</strong><br>
          Bolla: ${this.displayCode}<br>
          <small>Lat: ${waybillLat}, Lng: ${waybillLng}</small>
        </div>
      `;
      
      waybillMarker.bindPopup(waybillPopup).openPopup();
    }

    // Fit bounds to ensure the this.client.clientMaximumRange m radius is always fully visible
    if (dealerLat && dealerLng) {
      // Calculate the bounds that include the this.client.clientMaximumRangem radius circle
      // Convert this.client.clientMaximumRangem to degrees (approximately 0.009 degrees at equator)
      const radiusInDegrees = this.client.clientMaximumRange / 111320; // 111320 meters per degree
      
      const bounds = L.latLngBounds([
        [dealerLat - radiusInDegrees, dealerLng - radiusInDegrees],
        [dealerLat + radiusInDegrees, dealerLng + radiusInDegrees]
      ]);
      
      // If waybill marker exists, extend bounds to include it
      if (waybillLat && waybillLng) {
        bounds.extend([waybillLat, waybillLng]);
      }
      
      this.map.fitBounds(bounds, { padding: [50, 50] });
    }

    setTimeout(() => {
      if (this.map) {
        this.map.invalidateSize();
      }
    }, 100);
  }

  // Funzionalità methods
  /**
   * Stampa tutte le etichette della bolla (tutti i colli)
   */
  printLabels(): void {
    if (!this.waybill) {
      this.notificationService.error('Nessuna bolla selezionata');
      return;
    }

    if (!this.allParcels || this.allParcels.length === 0) {
      this.notificationService.error('Nessun collo disponibile per questa bolla');
      return;
    }

    // Genera tutte le etichette della bolla
    this.labelGenerationService.generateWaybillLabels(this.allParcels, this.waybill, this.dealer)
      .subscribe({
        next: (success) => {
          if (success) {
            const count = this.allParcels.length;
            const message = count === 1 
              ? `Etichetta generata per bolla ${this.waybill.waybillCode}`
              : `Etichette generate per bolla ${this.waybill.waybillCode} (${count} colli)`;
            this.notificationService.success(message);
          } else {
            this.notificationService.error('Errore nella generazione delle etichette');
          }
        },
        error: (error) => {
          console.error('Error generating labels:', error);
          this.notificationService.error('Errore nella generazione delle etichette');
        }
      });
  }

  /**
   * Stampa il documento della bolla (PDF completo)
   */
  printWaybill(): void {
    if (!this.waybill) {
      this.notificationService.error('Nessuna bolla disponibile');
      return;
    }

    if (!this.waybill.waybillCode) {
      this.notificationService.error('Codice bolla non disponibile');
      return;
    }

    // Usa il servizio shipmentManagementService per generare il PDF
    this.shipmentManagementService.printWaybills([{ key: this.waybill.waybillCode, value: this.waybill.waybillShipmentCode }])
      .subscribe({
        next: (blob) => {
          this.notificationService.success(`PDF della bolla ${this.waybill.waybillCode} generato con successo`);
        },
        error: (error) => {
          console.error('Error generating waybill PDF:', error);
          this.notificationService.error('Errore nella generazione del PDF della bolla');
        }
      });
  }

  /**
   * Stampa il documento POD (Proof of Delivery) della bolla
   */
  printPod(): void {
    if (!this.waybill) {
      this.notificationService.error('Nessuna bolla disponibile');
      return;
    }

    if (!this.waybill.waybillCode) {
      this.notificationService.error('Codice bolla non disponibile');
      return;
    }

    // Scarica il documento POD dal backend
    this.shipmentManagementService.downloadWaybillPod(this.waybill.waybillCode, this.waybill.waybillShipmentCode)
      .subscribe({
        next: (response: any) => {
          const blob = new Blob([response], { type: 'application/pdf' });
          const timestamp = new Date().toISOString()
            .replace(/[:.]/g, '-')
            .substring(0, 19);
          const filename = `POD_${this.waybill.waybillCode}_${timestamp}.pdf`;

          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          this.notificationService.success(`Documento POD della bolla ${this.waybill.waybillCode} generato con successo`);
        },
        error: (error) => {
          console.error('Error generating POD PDF:', error);
          this.notificationService.error('Errore nella generazione del documento POD');
        }
      });
  }

  /**
   * Apre il popup Axylog per inviare la bolla ad Axylog
   */
  sendToAxylog(): void {
    if (!this.waybill || !this.waybill.waybillCode) {
      this.notificationService.error('Nessuna bolla disponibile per l\'invio in Axylog');
      return;
    }

    // Emetti l'evento per aprire il popup Axylog nel componente padre
    this.openAxylogPopup.emit([this.waybill.waybillCode]);
  }

  /**
   * Apre il popup Mail per inviare la bolla via email
   */
  sendByEmail(): void {
    if (!this.waybill || !this.waybill.waybillCode) {
      this.notificationService.error('Nessuna bolla disponibile per l\'invio via email');
      return;
    }

    // Emetti l'evento per aprire il popup Mail nel componente padre
    this.openMailPopup.emit([this.waybill.waybillCode]);
  }

  /**
   * Invia il tracking code alle email configurate
   */
  sendTrackingCode(): void {
    if (!this.waybill) {
      this.notificationService.error('Nessuna bolla disponibile');
      return;
    }

    if (this.trackingEmails.length === 0) {
      this.notificationService.error('Nessuna email configurata per l\'invio del tracking code');
      return;
    }

    const emailList = this.trackingEmails.join(', ');
    
    // TODO: Implementare l'invio effettivo del tracking code
    // Per ora mostriamo una notifica con le email configurate
    this.notificationService.success(
      `Tracking code della bolla ${this.waybill.waybillCode} pronto per l'invio a: ${emailList}`
    );
    
    // Qui andrebbe implementata la chiamata al backend per inviare il tracking code
    console.log('Sending tracking code to emails:', this.trackingEmails);
    console.log('Waybill:', this.waybill);
  }
}
